import React from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import cx from 'classnames';
import { isEmpty } from 'lodash';
import { getAppointment } from 'selectors/appointment';
import { getServicesInAppointment } from 'selectors/services';

import { useBookingSteps } from 'hooks/book/useBookingSteps';
import { useAppContext } from 'hooks/context/useAppContext';

import { TAppointment } from 'types/appointment';

import Success from '../bookSuccess/Success';
import { AutoBookingErrorModal } from './AutoBookingErrorModal';
import { Contacts } from './Contacts/Contacts';
import Error from './Error';
import Payment from './Payment';
import { Progress } from './Progress';
import SelectDate from './SelectDate';
import { SelectLocation } from './SelectLocation/SelectLocation';
import SelectTime from './SelectTime/SelectTime';

import s from './Book.module.css';

const steps = [
  {
    key: 'select-date',
    title: 'Select a Day',
    screen: SelectDate,
    validate: (appointment: TAppointment) => !!appointment.date,
  },
  {
    key: 'select-time',
    title: 'Select a Time',
    screen: SelectTime,
    validate: (appointment: TAppointment) => !!appointment.time,
  },
  {
    key: 'select-location',
    title: 'Select Location',
    screen: SelectLocation,
    validate: (appointment: TAppointment) =>
      appointment.inHouse !== null &&
      (!appointment.inHouse ||
        (appointment.inHouse && !!appointment.addressLine1)),
  },
  {
    key: 'personal-info',
    title: 'Your Information',
    screen: Contacts,
    validate: (x: any) => !!x.name && !!x.phone && !!x.email,
  },
  {
    key: 'payment',
    title: 'Payment Information',
    screen: Payment,
    validate: (x: any) => !!x.cardNum && !!x.cardExp && !!x.cardCVC,
  },
  {
    key: 'success',
    screen: Success,
    validate: () => false,
    hideNext: true,
    isComplete: true,
  },
  {
    key: 'error',
    screen: Error,
    validate: () => false,
    hideNext: true,
    isComplete: true,
  },
];

export const Book = () => {
  const router = useRouter();
  const { publicUser } = useAppContext();
  const { multi_user: isMultiUser } = publicUser;
  const { navigateToNextStep } = useBookingSteps();

  const appointment = useSelector(getAppointment);

  const selectedServices = useSelector(getServicesInAppointment);

  const hasServicesSelectionStarted = !isEmpty(selectedServices);

  React.useEffect(() => {
    if (!appointment || isEmpty(appointment)) {
      if (isMultiUser && !hasServicesSelectionStarted) {
        void router.replace('/booking-flow');
      } else {
        void router.replace('/services');
      }
    }
  }, [appointment, isMultiUser, hasServicesSelectionStarted, router]);

  const getStep = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const thisStep = steps.find((item) => item.key === urlParams.get('step'));

      if (thisStep) {
        return thisStep;
      }
    }
    return steps[appointment?.stepIndex || 0];
  };

  const step = getStep();

  // Add effect to scroll to top when step changes
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [step?.key]);

  if (!step || !appointment) {
    return null;
  }

  return (
    <main className={cx(s.root, step.isComplete ? s.centerContent : '')}>
      <AutoBookingErrorModal />
      <div className={cx(step.isComplete ? s.hide : '')}>
        <Progress />
      </div>

      <h1 className={cx(s.title, !step.title ? s.hide : '')}>{step.title}</h1>
      {React.createElement(step.screen, { navigateToNextStep })}
    </main>
  );
};
